// @ts-nocheck

import React, { useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import notification from 'antd/lib/notification';

import PageHeader from './PageHeader';
import type { CommonFormProps } from './CommonForm';

import { Section } from '../../../layout';
import { SectionContent } from '../components/elements';
import { notificationLoading } from '../../../Notifications';

type AccessProps = {
  edit?: boolean | (() => void | Promise<void>);
  add?: boolean | (() => void | Promise<void>);
  delete?: boolean | (() => void | Promise<void>);
};

export type PageHeaderProps = {
  access?: AccessProps;
  breadCrumbs?: Array<{ to?: string; name?: string }>;
  mainHeader?: string | ReactNode;
  leftHeader?: ReactNode;
  rightHeader?: ReactNode;
};

type CardPageChild<T> =
  | ReactNode
  | ((props: Pick<CommonFormProps<T>, 'onSubmit' | 'onCancel'>) => ReactNode);

type CardPageProps<T> = {
  // Запрос данных
  onFetch?: () => void | Promise<void>;
  // Отправка данных
  onSubmit?: (values: T) => void | Promise<void>;
  // Отменить измененный данные
  onCancel?: (values: T) => void | Promise<void>;
  // пропсы заголовка страницы
  pageHeaderProps: PageHeaderProps;
  //
  children: CardPageChild<T>;
};

/**
 *  Карточка страницы
 */
export const CardWrapper = (props: {
  children: ReactNode;
  sectionDisabled?: boolean;
}) => {
  return (
    <Section>
      <SectionContent>{props.children}</SectionContent>
    </Section>
  );
};

export default function CardPage<T>({
  onFetch,
  ...props
}: CardPageProps<T>) {
  // Метод запроса данных с сервера
  const handleFetch = useCallback(async () => {
    try {
      await onFetch?.();
    } catch (error) {
      notification.error({
        message: 'Ошибка',
        description: error instanceof Error ? error.message : String(error),
      });
    }
  }, [onFetch]);

  // Загрузка данных при первой инициализации
  useEffect(() => {
    handleFetch();
  }, [handleFetch]);

  // Метод Отправки данных на сервер
  const onSubmit = useCallback(
    async (payload: T) => {
      try {
        notificationLoading({
          message: 'Сохранение данных...',
          key: 'saving',
        });
        await props.onSubmit?.(payload);
      } catch (error) {
        notification.error({
          message: 'Ошибка',
          description: error instanceof Error ? error.message : String(error),
        });
      } finally {
        notification.destroy('saving');
      }
    },
    [props.onSubmit]
  );

  // Метод отмены внесенных изменений
  const onCancel = useCallback(
    async (payload: T) => {
      try {
        await props.onCancel?.(payload);
      } catch (error) {
        notification.error({
          message: 'Ошибка',
          description: error instanceof Error ? error.message : String(error),
        });
      }
    },
    [props.onCancel]
  );

  /**
   *  Обертка для children. Обертывает каждый элемент в "карточки"
   *  В случае, если встречает объект со свойством noWrapMe, то не оборачивает его
   */
  const Content = useCallback(() => {
    const resolvedChildren =
      typeof props.children === 'function'
        ? props.children({ onSubmit, onCancel })
        : props.children;

    return React.Children.map(resolvedChildren as ReactNode, (child) => {
      const content = React.isValidElement(child)
        ? React.cloneElement(
            child as React.ReactElement<{
              onSubmit?: typeof onSubmit;
              onCancel?: typeof onCancel;
              noWrapMe?: boolean;
            }>,
            { onSubmit, onCancel }
          )
        : child;

      if (
        React.isValidElement<{
          noWrapMe?: boolean;
        }>(content) &&
        content.props.noWrapMe
      ) {
        return content;
      }
      return <CardWrapper>{content || null}</CardWrapper>;
    });
  }, [onCancel, onSubmit, props.children]);

  return (
    <>
      <PageHeader {...props.pageHeaderProps} />
      <Content />
    </>
  );
}
